import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "../../../common/DataTable";
import Pagination from "../../../common/Pagination";
import BASE_URL from "../../../../config/config";
import "../../../../css/PostList.css";

const debounce = (func, delay) => {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => func.apply(this, args), delay);
  };
};

export default function TagList() {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });
  const navigate = useNavigate();
  const [sortConfig, setSortConfig] = useState({
    key: "created_at",
    direction: "desc",
  });
  const [csrfToken, setCsrfToken] = useState("");

  // Search states
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const searchInputRef = useRef(null);

  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearchTerm(value);
      setPagination(prev => ({ ...prev, currentPage: 1 }));
    }, 500),
    []
  );

  const handleSearchChange = (value) => {
    setSearchInput(value);
    debouncedSearch(value);
  };

  const fetchTags = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
        ...(searchTerm && { search: searchTerm }),
      }).toString();

      const response = await fetch(`${BASE_URL}/blog/tags?${queryParams}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) throw new Error("خطا در دریافت تگ‌ها");

      const data = await response.json();
      setTags(data.data || data);
      setPagination((prev) => ({
        ...prev,
        totalPages: data.pagination?.totalPages || 1,
        totalItems: data.pagination?.totalItems || (data.data || data).length,
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchToken = async () => {
      try {
        const res = await fetch(`${BASE_URL}/csrf-token`, { credentials: "include" });
        const data = await res.json();
        setCsrfToken(data.csrfToken);
      } catch {
        setError("دریافت توکن امنیتی با خطا");
      }
    };
    fetchToken();
  }, []);

  useEffect(() => {
    fetchTags();
  }, [pagination.currentPage, searchTerm]);

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  const handleEdit = (tagId) => {
    navigate(`/admin/blog/tags/edit/${tagId}`);
  };

  const handleDelete = async (tagId) => {
    if (!window.confirm("آیا از حذف این تگ مطمئن هستید؟")) return;

    try {
      const response = await fetch(`${BASE_URL}/blog/tags/${tagId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "CSRF-Token": csrfToken,
        },
        credentials: "include",
      });

      const data = await response.json();
    
      if (!response.ok) {
        throw new Error(data.error || "خطا در حذف تگ");
      }

      alert(data.message || "تگ با موفقیت حذف شد");
      fetchTags();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSort = (newSortConfig) => {
    setSortConfig(newSortConfig);
    const sortedData = [...tags].sort((a, b) => {
      if (a[newSortConfig.key] < b[newSortConfig.key]) {
        return newSortConfig.direction === "asc" ? -1 : 1;
      }
      if (a[newSortConfig.key] > b[newSortConfig.key]) {
        return newSortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
    setTags(sortedData);
  };

  const columns = [
    {
      key: "name",
      label: "نام تگ",
      render: (value) => <span className="tag-name">{value}</span>,
    },
    {
      key: "slug",
      label: "Slug",
      render: (value) => <span className="tag-slug">{value}</span>,
    },
    {
      key: "created_at",
      label: "تاریخ ایجاد",
      render: (value) => value ? new Date(value).toLocaleDateString("fa-IR") : '-',
    },
    {
      key: "updated_at",
      label: "آخرین ویرایش",
      render: (value) => value ? new Date(value).toLocaleDateString("fa-IR") : '-',
    },
  ];

  if (loading) return <div className="loading">در حال بارگذاری...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="tag-list-container">
      <div className="tag-list-header">
        <h2>مدیریت تگ‌ها</h2>
        <button
          className="btn-primary"
          onClick={() => navigate("/admin/blog/tags/add")}
        >
          افزودن تگ جدید
        </button>
      </div>

      <div className="filters-container">
        <div className="search-box">
          <input
            type="text"
            ref={searchInputRef}
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="جستجو در نام یا slug..."
            className="search-input"
          />
          {searchTerm && (
            <button 
              className="clear-search-btn"
              onClick={() => {
                setSearchInput("");
                setSearchTerm("");
              }}
            >
              پاک کردن
            </button>
          )}
        </div>
      </div>

      <DataTable
        columns={columns}
        data={tags}
        sortConfig={sortConfig}
        onSort={handleSort}
        renderActions={(tag) => (
          <div className="actions">
            <button 
              className="btn-edit" 
              onClick={() => handleEdit(tag.id)}
            >
              ویرایش
            </button>
            <button
              className="btn-delete"
              onClick={() => handleDelete(tag.id)}
            >
              حذف
            </button>
          </div>
        )}
      />

      {pagination.totalPages > 1 && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
}